import { apiClient } from './client';
import {
   Courrier,
   CourrierListResponse,
   CreateCourrierRequest,
   UpdateCourrierRequest,
   CourrierFilters,
   AssignCourrierRequest,
   TransitionCourrierRequest,
   PieceJointe,
   OCRResult,
   Organisation,
   OrganisationDefaults,
 } from '../../types/api';

/** Normalisation des valeurs issues de l'UI (fr) vers les enums backend (Prisma) */
const STATUS_MAP: Record<string, string> = {
  recu: 'RECU',
  en_validation: 'EN_VALIDATION',
  incomplet: 'INCOMPLET',
  en_attente_signature: 'EN_ATTENTE_SIGNATURE',
  signe: 'SIGNE',
  envoye: 'ENVOYE',
  classe: 'CLASSE',
  rejete: 'REJETE',
};

const TYPE_MAP: Record<string, string> = {
  entrant: 'ENTRANT',
  sortant: 'SORTANT',
  interne: 'INTERNE',
};

const PRIORITY_MAP: Record<string, string> = {
  urgente: 'URGENT',
  haute: 'HIGH',
  normale: 'NORMAL',
  basse: 'LOW',
};

function normalizeStatus(v?: string): string | undefined {
  if (!v) return undefined;
  const key = v.toLowerCase();
  return STATUS_MAP[key] ?? v.toUpperCase();
}

function normalizeType(v?: string): string | undefined {
  if (!v) return undefined;
  const key = v.toLowerCase();
  return TYPE_MAP[key] ?? v.toUpperCase();
}

function normalizePriority(v?: string): string | undefined {
  if (!v) return undefined;
  const key = v.toLowerCase();
  return PRIORITY_MAP[key] ?? v.toUpperCase();
}

export class CourrierService {
  /**
   * Récupérer la liste des courriers avec filtres et pagination
   */
  async getCourriers(
    filters?: CourrierFilters,
    page: number = 1,
    limit: number = 20,
  ): Promise<CourrierListResponse> {
    console.log('📡 [COURRIER SERVICE] Requête GET /courriers:', { filters, page, limit });

    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (filters) {
      if (filters.organisationId) params.append('organisationId', filters.organisationId.toString());
      if (filters.status) params.append('status', normalizeStatus(filters.status)!);
      if (filters.type) params.append('type', normalizeType(filters.type)!);
      if (filters.priority) params.append('priority', normalizePriority(filters.priority)!);
      if (filters.assigned_to) params.append('assigned_to', filters.assigned_to.toString());
      if (filters.created_by) params.append('created_by', filters.created_by.toString());
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      if (filters.q) params.append('q', filters.q);
    }

    console.log('📡 [COURRIER SERVICE] URL complète:', `/courriers?${params}`);

    try {
      const response = await apiClient.get<CourrierListResponse>(`/courriers?${params}`);

      console.log('📡 [COURRIER SERVICE] Réponse reçue:', {
        total: response.total,
        count: response.data?.length || 0,
        data: response.data,
        page: response.page,
        totalPages: response.totalPages
      });

      return response;
    } catch (error: any) {
      console.error('❌ [COURRIER SERVICE] Erreur API:', error);
      console.error('❌ [COURRIER SERVICE] Erreur détails:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
        config: error.config
      });
      throw error;
    }
  }

  /**
   * Récupérer un courrier par son ID
   */
  async getCourrier(id: number): Promise<Courrier> {
    return await apiClient.get<Courrier>(`/courriers/${id}`);
  }

  /**
   * Créer un nouveau courrier
   */
  async createCourrier(courrier: CreateCourrierRequest): Promise<Courrier> {
    return await apiClient.post<Courrier>('/courriers', courrier);
  }

  /**
   * Mettre à jour un courrier
   */
  async updateCourrier(id: number, updates: UpdateCourrierRequest): Promise<Courrier> {
    return await apiClient.patch<Courrier>(`/courriers/${id}`, updates);
  }

  /**
   * Supprimer un courrier
   */
  async deleteCourrier(id: number): Promise<void> {
    await apiClient.delete(`/courriers/${id}`);
  }

  /**
   * Assigner un courrier à un utilisateur
   */
  async assignCourrier(id: number, assignment: AssignCourrierRequest): Promise<Courrier> {
    return await apiClient.post<Courrier>(`/courriers/${id}/assign`, assignment);
  }

  /**
   * Changer l'état d'un courrier (workflow)
   */
  async transitionCourrier(id: number, transition: TransitionCourrierRequest): Promise<Courrier> {
    return await apiClient.post<Courrier>(`/courriers/${id}/transition`, transition);
  }

  /**
   * Récupérer les pièces jointes d'un courrier
   */
  async getPiecesJointes(courrierId: string): Promise<PieceJointe[]> {
    return await apiClient.get<PieceJointe[]>(`/courriers/${courrierId}/pieces`);
  }

  /**
   * Télécharger une pièce jointe
   */
  async downloadPieceJointe(pieceId: string, filename?: string): Promise<void> {
    await apiClient.downloadFile(`/pieces/${pieceId}/file`, filename);
  }

  /**
   * Récupérer le texte OCR d'une pièce jointe
   */
  async getOCRText(pieceId: string): Promise<OCRResult> {
    return await apiClient.get<OCRResult>(`/pieces/${pieceId}/ocr`);
  }

  /**
   * Déclencher l'OCR manuellement pour une pièce jointe
   */
  async triggerOCR(pieceId: string): Promise<{ jobId: string }> {
    return await apiClient.post<{ jobId: string }>(`/pieces/${pieceId}/ocr`);
  }

  /**
   * Rechercher des courriers
   */
  async searchCourriers(
    query: string,
    filters?: CourrierFilters,
    page: number = 1,
    limit: number = 20,
  ): Promise<CourrierListResponse> {
    const params = new URLSearchParams({
      q: query,
      page: page.toString(),
      limit: limit.toString(),
    });

    if (filters) {
      if (filters.status) params.append('status', normalizeStatus(filters.status)!);
      if (filters.type) params.append('type', normalizeType(filters.type)!);
      if (filters.priority) params.append('priority', normalizePriority(filters.priority)!);
      if (filters.assigned_to) params.append('assigned_to', filters.assigned_to.toString());
      if (filters.created_by) params.append('created_by', filters.created_by.toString());
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
    }

    return await apiClient.get<CourrierListResponse>(`/search?${params}`);
  }

  /**
   * Récupérer les statistiques des courriers
   */
  async getStatistics(): Promise<{
    total: number;
    byStatus: Record<string, number>;
    byPriority: Record<string, number>;
    recentActivity: number;
  }> {
    try {
      // Essayer d'abord de récupérer les statistiques du backend
      const response = await apiClient.get('/courriers/statistics');
      console.log('📊 [STATISTICS] Réponse brute du backend:', response);

      // Si le backend renvoie des données valides, les utiliser
      if (response && response.courriers_by_status && Object.keys(response.courriers_by_status).length > 0) {
        console.log('📊 [STATISTICS] Utilisation des données du backend');

        // Mapping direct des statuts anglais vers français
        const statusMapping: Record<string, string> = {
          'RECU': 'recu',
          'EN_VALIDATION': 'envalidation',
          'SIGNE': 'signe',
          'ENVOYE': 'envoye',
          'CLASSE': 'classe',
          'REJETE': 'rejete'
        };

        // Mapping direct des priorités anglaises vers français
        const priorityMapping: Record<string, string> = {
          'URGENT': 'urgent',
          'HIGH': 'high',
          'NORMAL': 'normal',
          'LOW': 'low'
        };

        // Normaliser les statuts avec mapping
        const normalizedStatus: Record<string, number> = {};
        Object.entries(response.courriers_by_status).forEach(([status, count]) => {
          const normalizedKey = statusMapping[status] || status.toLowerCase().replace('_', '');
          normalizedStatus[normalizedKey] = count as number;
        });

        // Normaliser les priorités avec mapping
        const normalizedPriority: Record<string, number> = {};
        Object.entries(response.courriers_by_priority).forEach(([priority, count]) => {
          const normalizedKey = priorityMapping[priority] || priority.toLowerCase();
          normalizedPriority[normalizedKey] = count as number;
        });

        return {
          total: response.total_courriers || 0,
          byStatus: normalizedStatus,
          byPriority: normalizedPriority,
          recentActivity: response.recent_activity || 0
        };
      } else {
        // Si le backend ne renvoie pas de données valides, calculer localement
        console.log('📊 [STATISTICS] Backend vide, calcul local des statistiques');
        return await this.calculateLocalStatistics();
      }
    } catch (error) {
      console.error('❌ [STATISTICS] Erreur backend, calcul local:', error);
      return await this.calculateLocalStatistics();
    }
  }

  private async calculateLocalStatistics(): Promise<{
    total: number;
    byStatus: Record<string, number>;
    byPriority: Record<string, number>;
    recentActivity: number;
  }> {
    try {
      // Récupérer tous les courriers pour calculer les statistiques localement avec une limite raisonnable
      const courriersResponse = await this.getCourriers({}, 1, 100);
      const courriers = courriersResponse.data || [];
      
      console.log('📊 [STATISTICS] Calcul local sur', courriers.length, 'courriers');
      
      // Si on n'a pas tous les courriers, on utilise ce qu'on a
      if (courriers.length < courriersResponse.total) {
        console.warn('⚠️ [STATISTICS] Calcul local partiel: seulement', courriers.length, 'courriers sur', courriersResponse.total);
      }

      // Initialiser les compteurs
      const byStatus: Record<string, number> = {
        recu: 0,
        envalidation: 0,
        signe: 0,
        envoye: 0,
        classe: 0,
        rejete: 0
      };

      const byPriority: Record<string, number> = {
        urgent: 0,
        high: 0,
        normal: 0,
        low: 0
      };

      // Compter les courriers par statut et priorité
      courriers.forEach(courrier => {
        // Compter par statut (normaliser en minuscules sans underscore)
        const statusKey = courrier.status.toLowerCase().replace('_', '');
        if (byStatus.hasOwnProperty(statusKey)) {
          byStatus[statusKey]++;
        } else {
          byStatus[statusKey] = 1;
        }

        // Compter par priorité (normaliser en minuscules)
        const priorityKey = courrier.priority.toLowerCase();
        if (byPriority.hasOwnProperty(priorityKey)) {
          byPriority[priorityKey]++;
        } else {
          byPriority[priorityKey] = 1;
        }
      });

      // Calculer l'activité récente (courriers des 7 derniers jours)
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      const recentActivity = courriers.filter(courrier =>
        new Date(courrier.createdAt) >= sevenDaysAgo
      ).length;

      const result = {
        total: courriersResponse.total || courriers.length,
        byStatus,
        byPriority,
        recentActivity
      };

      console.log('📊 [STATISTICS] Statistiques calculées localement:', result);
      return result;
    } catch (error) {
      console.error('❌ [STATISTICS] Erreur calcul local, données par défaut:', error);
      return this.getDefaultStatistics();
    }
  }

  private getDefaultStatistics() {
    return {
      total: 0,
      byStatus: {
        recu: 0,
        envalidation: 0,
        signe: 0,
        envoye: 0,
        rejete: 0
      },
      byPriority: {
        urgent: 0,
        high: 0,
        normal: 0,
        low: 0
      },
      recentActivity: 0
    };
  }

  /**
   * Récupérer l'historique des événements d'un courrier
   */
  async getCourrierHistory(courrierId: string): Promise<any[]> {
    return await apiClient.get<any[]>(`/courriers/${courrierId}/history`);
  }

  /**
   * Récupérer les organisations disponibles
   */
  async getOrganisations(): Promise<Organisation[]> {
    return await apiClient.get<Organisation[]>('/organisations');
  }

  /**
   * Récupérer les paramètres par défaut de l'utilisateur
   */
  async getUserDefaults(): Promise<OrganisationDefaults> {
    return await apiClient.get<OrganisationDefaults>('/user/defaults/');
  }

  /**
   * Mettre à jour les paramètres par défaut de l'utilisateur
   */
  async updateUserDefaults(defaults: OrganisationDefaults): Promise<OrganisationDefaults> {
    return await apiClient.put<OrganisationDefaults>('/user/defaults', defaults);
  }
}

// Export singleton instance
export const courrierService = new CourrierService();